import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Table } from 'react-bootstrap';
import { Snackbar, Alert } from '@mui/material';
import CreatableSelect from "react-select/creatable";
import "./apiKeys.scss";
import "./companyMembers.scss";
import { getCompanies, getCompanyMembers, addCompanyMember, getCompanyRoles, getModules, updateMemberStatus, getMemberById } from '../../Redux/ActionCreators/settingAction';
import { getUsers, addUser } from '../../Redux/ActionCreators/userAction';
import ToggleSwitch from '../../utils/toggleSwitch';

const renderCustomPagination = (totalPages, currentPage, setCurrentPage) => {
    const pagination = [];

    // Previous button
    pagination.push(
        <button
            className="prev-btn"
            key="prev"
            onClick={() => setCurrentPage(currentPage - 1)}
            disabled={currentPage === 1}
        >
            <img src="/images/leftarrow-icon.svg" alt="" />
        </button>
    );

    // Show first page ONLY if close to the start
    if (currentPage <= 3) {
        pagination.push(
            <button
                key={1}
                className={currentPage === 1 ? "active" : ""}
                onClick={() => setCurrentPage(1)}
            >
                {1}
            </button>
        );
    } else {
        pagination.push(<button key="left-dots">...</button>);
    }

    // Middle numbers
    const startNum = Math.max(2, currentPage - 1);
    const endNum = Math.min(totalPages - 1, currentPage + 1);

    for (let i = startNum; i <= endNum; i++) {
        pagination.push(
            <button
                key={i}
                className={currentPage === i ? "active" : ""}
                onClick={() => setCurrentPage(i)}
            >
                {i}
            </button>
        );
    }

    // Show last page ONLY if close to the end
    if (currentPage >= totalPages - 2) {
        if (totalPages > 1) {
            pagination.push(
                <button
                    key={totalPages}
                    className={currentPage === totalPages ? "active" : ""}
                    onClick={() => setCurrentPage(totalPages)}
                >
                    {totalPages}
                </button>
            );
        }
    } else {
        pagination.push(<button key="right-dots">...</button>);
    }

    // Next button
    pagination.push(
        <button
            className="next-btn"
            key="next"
            onClick={() => setCurrentPage(currentPage + 1)}
            disabled={currentPage === totalPages}
        >
            <img src="/images/leftarrow-icon.svg" alt="" style={{ rotate: "180deg" }} />
        </button>
    );

    return pagination;
};

// Removed hardcoded ROLES – roles are now fetched dynamically from the API

const defaultForm = {
    companyId: '',
    fullName: '',
    email: '',
    role: '',
    isActive: true,
};

const CompanyMembers = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState('details');
    const [form, setForm] = useState(defaultForm);
    const [selectedPermIds, setSelectedPermIds] = useState(new Set());
    const [formErrors, setFormErrors] = useState({});
    const [snack, setSnack] = useState({ open: false, message: '', severity: 'info' });
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [rolesLoading, setRolesLoading] = useState(false);
    const [popupLoading, setPopupLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const SESSION_PAGE_SIZE_KEY = useMemo(() => `companyMembers_pageSize`, []);
    const SESSION_CURRENT_PAGE_KEY = useMemo(() => `companyMembers_currentPage`, []);

    const safeGet = (key, fallback) => {
        try {
            const value = Number(window?.sessionStorage?.getItem(key));
            return Number.isFinite(value) && value > 0 ? value : fallback;
        } catch {
            return fallback;
        }
    };

    const [currentPage, setCurrentPage] = useState(() => safeGet(SESSION_CURRENT_PAGE_KEY, 1));
    const [pageSize, setPageSize] = useState(() => safeGet(SESSION_PAGE_SIZE_KEY, 10));
    const isMobile = window.innerWidth < 767;

    const companies = useSelector(state => state.setting.companies || []);
    const companyMembers = useSelector(state => state.setting.companyMembers || []);
    const companyMembersTotal = useSelector(state => state.setting.companyMembersTotal || 0);
    const companyRoles = useSelector(state => state.setting.companyRoles || []);
    const modules = useSelector(state => state.setting.modules || []);
    const usersList = useSelector(state => state.users.usersList || []);

    const selectStyles = {
        control: (base) => ({ ...base, minWidth: 167, borderRadius: "4px", color: "#1f2a37", fontSize: "14px", lineHeight: "18px", fontFamily: '"Inter", sans-serif' }),
        menu: (base) => ({ ...base, minWidth: 167, fontFamily: '"Inter", sans-serif' }),
        menuList: (base) => ({ ...base, overflowX: "hidden" }),
        placeholder: (base) => ({ ...base, color: "#1f2a37", fontFamily: '"Inter", sans-serif' }),
        option: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        singleValue: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
        input: (base) => ({ ...base, fontFamily: '"Inter", sans-serif' }),
    };

    useEffect((pageSize = 200,) => {
        dispatch(getCompanies(pageSize));
    }, []);

    useEffect(() => {
        sessionStorage.setItem(SESSION_PAGE_SIZE_KEY, pageSize);
    }, [pageSize, SESSION_PAGE_SIZE_KEY]);

    useEffect(() => {
        sessionStorage.setItem(SESSION_CURRENT_PAGE_KEY, currentPage);
    }, [currentPage, SESSION_CURRENT_PAGE_KEY]);

    useEffect(() => {
        fetchMembers(selectedCompanyId, pageSize, 1);
        setCurrentPage(1);
    }, [selectedCompanyId, pageSize]);

    const fetchMembers = async (companyId, pageSize, page = 1) => {
        setLoading(true);
        await dispatch(getCompanyMembers(companyId, pageSize, page));
        setLoading(false);
    };

    const handlePageChange = (page) => {
        setCurrentPage(page);
        fetchMembers(selectedCompanyId, pageSize, page);
    };

    const handleToggleMemberStatus = async (member) => {
        const newStatus = !member.is_active;
        const result = await dispatch(updateMemberStatus(member.id, newStatus));
        if (result && result.type === 'UPDATE_MEMBER_STATUS') {
            // Refresh just this row with latest data from the API
            await dispatch(getMemberById(member.id));
        } else {
            setSnack({ open: true, message: 'Failed to update User status.', severity: 'error' });
        }
    };

    const handleAddClick = async () => {
        setShowModal(true);
        setPopupLoading(true);
        setForm({ ...defaultForm, companyId: selectedCompanyId });
        setSelectedPermIds(new Set());
        setFormErrors({});
        setActiveTab('details');
        setRolesLoading(true);
        await Promise.all([
            dispatch(getModules()),
            dispatch(getUsers('', 1, 100)),
            ...(selectedCompanyId ? [dispatch(getCompanyRoles(selectedCompanyId))] : []),
        ]);
        setRolesLoading(false);
        setPopupLoading(false);
    };

    const validateDetails = () => {
        const errors = {};
        if (!form.companyId) errors.companyId = 'Please select a company';
        if (!form.fullName.trim()) errors.fullName = 'Please enter full name';
        if (!form.email.trim()) errors.email = 'Please enter email address';
        if (!form.role) errors.role = 'Please assign a role';
        return errors;
    };

    const handleSave = async () => {
        setSaving(true);
        const errors = validateDetails();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            setActiveTab('details');
            setSaving(false);
            return;
        }

        // Try to match the entered email against existing users (emailId field)
        const normalizedEmail = form.email.trim().toLowerCase();
        const matchedUser = usersList.find(
            u => (u.emailId || u.email || '').toLowerCase() === normalizedEmail
        );

        let resolvedUserId;

        try {
            if (matchedUser) {
                // Email matched an existing user – use their userID directly
                resolvedUserId = matchedUser.userID;
            } else {
                // No match – create a new user first
                const newUserResult = await dispatch(addUser({
                    name: form.fullName.trim(),
                    emailId: form.email.trim(),
                }));

                if (!newUserResult || newUserResult.type !== 'ADD_USERS') {
                    setSnack({ open: true, message: 'Failed to create user either user or email already exists', severity: 'error' });
                    return;
                }

                // API response: { userId: 22270, responseMessages: [...] }
                resolvedUserId = newUserResult.payload?.userId
                    || newUserResult.payload?.userID
                    || newUserResult.payload?.id;

                if (!resolvedUserId) {
                    setSnack({ open: true, message: 'Could not retrieve new user.', severity: 'error' });
                    return;
                }
            }

            const payload = {
                user_id: resolvedUserId,
                role_id: form.role,
                is_primary: false,
                is_active: true,
            };

            const effectiveCompanyId = form.companyId || selectedCompanyId || 'default';
            const result = await dispatch(addCompanyMember(effectiveCompanyId, payload));
            if (result && result.type === 'ADD_COMPANY_MEMBER') {
                setShowModal(false);
                setSnack({ open: true, message: 'User added successfully!', severity: 'success' });
                fetchMembers(selectedCompanyId, pageSize, 1);
            } else {
                setSnack({ open: true, message: 'Failed to add User.', severity: 'error' });
            }
        } catch (error) {
            setSnack({ open: true, message: 'Something went wrong.', severity: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const totalPages = Math.ceil(companyMembersTotal / pageSize) || 1;
    const pagedMembers = companyMembers;

    return (
        <div className="job-table-container">

            <div className="top-navigation">
                <div className='createjob-tab'>
                    <h4>Company Users</h4>
                    <button className="create-job" onClick={handleAddClick} style={{ width: 'unset' }}>
                        Add Users
                    </button>
                </div>
            </div>

            <div className="table-content" style={{ background: '#FFFFFF', marginBottom: '70px', borderRadius: '8px' }}>
                <div className="controls">
                    <div className="has-search">
                    </div>
                    <div className="filter-buttons">
                        <CreatableSelect
                            inputId="company-select"
                            value={
                                selectedCompanyId
                                    ? companies
                                        .map((company) => ({
                                            value: company.id,
                                            label: company.name || company.companyName || company.display_name || company.id,
                                        }))
                                        .find((c) => c.value === selectedCompanyId) || null
                                    : null
                            }
                            onChange={(option) => setSelectedCompanyId(option ? option.value : "")}
                            options={companies.map((company) => ({
                                value: company.id,
                                label: company.name || company.companyName || company.display_name || company.id,
                            }))}
                            isClearable
                            placeholder="Select a Company"
                            maxMenuHeight={200}
                            classNamePrefix="rs-filter"
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                            styles={selectStyles}
                        />
                    </div>
                </div>
                <Table responsive className="job-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Company</th>
                            <th>Active</th>
                            <th>Role</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="4" className="text-center" style={{ color: '#C3C3C3', fontStyle: 'italic' }}>Loading...</td></tr>
                        ) : companyMembers.length === 0 ? (
                            <tr><td colSpan="4" className="text-center" style={{ color: '#C3C3C3', fontStyle: 'italic' }}>No Users found</td></tr>
                        ) : (
                            pagedMembers.map(member => (
                                <tr key={member.id}>
                                    <td>{member.name || '—'}</td>
                                    <td>{member.email || '—'}</td>
                                    <td>{member.company?.name || '—'}</td>
                                    <td>
                                        <ToggleSwitch
                                            checked={!!member.is_active}
                                            onChange={() => handleToggleMemberStatus(member)}
                                        />
                                    </td>
                                    <td>{member.roles?.[0]?.name || '—'}</td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </Table>
                {!loading && (
                    <div className="pagination d-flex flex-wrap align-items-center justify-content-between p-3">
                        <div className="d-flex flex-wrap" style={{ gap: isMobile ? "10px" : '40px' }}>
                            <div className="d-flex flex-wrap" style={{ gap: isMobile ? "10px" : '20px' }}>
                                <div className="pagination-info d-flex align-items-center">
                                    {companyMembersTotal ? (
                                        <span style={{ color: '#525B66' }}>
                                            Showing {(currentPage - 1) * pageSize + 1}-
                                            {Math.min(currentPage * pageSize, companyMembersTotal)} of{" "}
                                            <strong>{companyMembersTotal}</strong> Members
                                        </span>
                                    ) : (
                                        <span>Showing 0-0 of 0</span>
                                    )}
                                </div>
                                <div className="pagination-buttons">
                                    {renderCustomPagination(totalPages, currentPage, handlePageChange)}
                                </div>
                            </div>
                            <div className="d-flex align-items-center">
                                <span style={{ color: '#525B66', lineHeight: '100%' }}>Users per page</span>&nbsp;&nbsp;&nbsp;&nbsp;
                                <select
                                    value={pageSize}
                                    onChange={(e) => {
                                        setPageSize(parseInt(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                    style={{ width: "46px", height: '32px', borderRadius: '2px', border: '1px solid #8A9199', color: '#8A9199' }}
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                </select>
                            </div>
                        </div>
                    </div>
                )}
            </div>

            {/* ADD MEMBER MODAL */}
            {showModal && (
                <div className="upload-modal automation-modal">
                    <div className="modal-content member-modal-content">
                        <div className="modal-head">
                            <h3>Add User</h3>
                            <img
                                src="/images/close-icon.svg"
                                alt="close"
                                onClick={() => setShowModal(false)}
                                style={{ cursor: 'pointer' }}
                            />
                        </div>

                        {popupLoading ?
                            <div
                                className="loading-overlay"
                                style={{ display: 'flex', justifyContent: 'center', alignItems: "center", height: "400px" }}
                            >
                                <div
                                    className="spinner-border"
                                    role="status"
                                    style={{ width: "1rem", height: "1rem" }}
                                >
                                    <span className="visually-hidden">
                                        Loading job...
                                    </span>
                                </div>
                            </div>
                            :
                            <>
                                <div className="modal-input">
                                    {/* Select Company */}
                                    <div className="automation-dropdown">
                                        <label>Select Company *</label>
                                        <CreatableSelect
                                            inputId="add-company-select"
                                            value={
                                                form.companyId
                                                    ? companies
                                                        .map((company) => {
                                                            console.log('Value map - Company value:', company.id, 'label:', company.name);
                                                            return {
                                                                value: company.id,
                                                                label: company.name
                                                            };
                                                        })
                                                        .find((c) => c.value === form.companyId) || null
                                                    : null
                                            }
                                            onChange={(option) => {
                                                const companyId = option ? option.value : "";
                                                setForm({ ...form, companyId, role: '' });
                                                setFormErrors({ ...formErrors, companyId: '', role: '' });
                                                if (companyId) {
                                                    dispatch(getCompanyRoles(companyId));
                                                }
                                            }}
                                            options={companies.map((company) => {
                                                console.log('Company value:', company.id, 'label:', company.name || company.companyName || company.display_name || company.id);
                                                return {
                                                    value: company.id,
                                                    label: company.name || company.companyName || company.display_name || company.id,
                                                };
                                            })}
                                            isClearable
                                            placeholder="Select a company"
                                            maxMenuHeight={200}
                                            classNamePrefix="rs-filter"
                                            styles={selectStyles}
                                        />
                                        {formErrors.companyId && (
                                            <span className="field-error">{formErrors.companyId}</span>
                                        )}
                                    </div>

                                    {/* Full Name */}
                                    <div className="automation-dropdown">
                                        <label>Full Name *</label>
                                        <input
                                            type="text"
                                            placeholder="Enter full name"
                                            value={form.fullName}
                                            onChange={e => {
                                                setForm({ ...form, fullName: e.target.value });
                                                setFormErrors({ ...formErrors, fullName: '' });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: formErrors.fullName ? '1px solid red' : '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none',
                                            }}
                                        />
                                        {formErrors.fullName && (
                                            <span className="field-error">{formErrors.fullName}</span>
                                        )}
                                    </div>

                                    {/* Email Address */}
                                    <div className="automation-dropdown">
                                        <label>Email Address *</label>
                                        <input
                                            type="email"
                                            placeholder="Enter email address"
                                            value={form.email}
                                            onChange={e => {
                                                setForm({ ...form, email: e.target.value });
                                                setFormErrors({ ...formErrors, email: '' });
                                            }}
                                            style={{
                                                width: '100%',
                                                padding: '8px 12px',
                                                border: formErrors.email ? '1px solid red' : '1px solid #d1d5db',
                                                borderRadius: '6px',
                                                fontSize: '14px',
                                                outline: 'none',
                                            }}
                                        />
                                        {formErrors.email && (
                                            <span className="field-error">{formErrors.email}</span>
                                        )}
                                    </div>

                                    {/* Assign Role */}
                                    <div className="automation-dropdown">
                                        <label>Assign Role *</label>
                                        <CreatableSelect
                                            inputId="add-role-select"
                                            value={
                                                form.role
                                                    ? companyRoles
                                                        .map((r) => ({
                                                            value: r.id,
                                                            label: r.name || r.role_name || r.title || r.id,
                                                        }))
                                                        .find((r) => r.value === form.role) || null
                                                    : null
                                            }
                                            onChange={(option) => {
                                                setForm({ ...form, role: option ? option.value : "" });
                                                setFormErrors({ ...formErrors, role: '' });
                                            }}
                                            options={companyRoles.map((r) => ({
                                                value: r.id,
                                                label: r.name || r.role_name || r.title || r.id,
                                            }))}
                                            isClearable
                                            isLoading={rolesLoading}
                                            placeholder={rolesLoading ? 'Loading roles...' : 'Select a role'}
                                            maxMenuHeight={200}
                                            classNamePrefix="rs-filter"
                                            styles={selectStyles}
                                        />
                                        {formErrors.role && (
                                            <span className="field-error">{formErrors.role}</span>
                                        )}
                                    </div>
                                </div>

                                {/* Footer */}
                                <div className="d-flex justify-content-end" style={{ margin: '0 30px 20px 30px' }}>
                                    <button
                                        className="upload-btn"
                                        onClick={() => setShowModal(false)}
                                        style={{ backgroundColor: 'transparent', border: '1px solid #111928', color: '#111928', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.5 : 1 }}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        className="upload-btn"
                                        onClick={handleSave}
                                        disabled={saving}
                                        style={{
                                            opacity: saving ? 0.5 : 1,
                                            cursor: saving ? 'not-allowed' : 'pointer',
                                        }}
                                    >
                                        {saving ? 'Add User...' : 'Add User'}

                                    </button>
                                </div>
                            </>
                        }
                    </div>
                </div>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack({ ...snack, open: false })}
            >
                <Alert severity={snack.severity}>{snack.message}</Alert>
            </Snackbar>
        </div>
    );
};

export default CompanyMembers;